// Date utilities
export const dateUtils = {
  formatDate: (date) => {
    if (!date) return "N/A";

    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  },

  formatDateTime: (date) => {
    if (!date) return "N/A";

    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  },

  isValidDate: (date) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj instanceof Date && !isNaN(dateObj);
  },
};

// Financial Year utilities
export const financialYearUtils = {
  calculateFinancialYear: (date = new Date()) => {
    const inputDate = date instanceof Date ? date : new Date(date);
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth() + 1;

    if (month >= 4) {
      return `${year + 1}-${String(year + 2).slice(-2)}`;
    } else {
      return `${year}-${String(year + 1).slice(-2)}`;
    }
  },

  getFinancialYearDescription: (fyCode) => {
    if (!fyCode) return "Not specified";

    try {
      const [startYear, endYearShort] = fyCode.split("-");
      const endYear = startYear.slice(0, 2) + endYearShort;
      return `FY ${fyCode}`;
    } catch (error) {
      return "Invalid FY";
    }
  },

  getFinancialYearStart: (fyCode) => {
    if (!fyCode) return null;

    try {
      const [startYear] = fyCode.split("-");
      return new Date(parseInt(startYear), 3, 1);
    } catch (error) {
      return null;
    }
  },

  getFinancialYearEnd: (fyCode) => {
    if (!fyCode) return null;

    try {
      const [startYear] = fyCode.split("-");
      const endYear = parseInt(startYear) + 1;
      return new Date(endYear, 2, 31);
    } catch (error) {
      return null;
    }
  },

  isDateInFinancialYear: (date, fyCode) => {
    const inputDate = date instanceof Date ? date : new Date(date);
    const calculatedFY = financialYearUtils.calculateFinancialYear(inputDate);
    return calculatedFY === fyCode;
  },

  getCurrentFinancialYear: () => {
    return financialYearUtils.calculateFinancialYear(new Date());
  },

  getFinancialYearOptions: () => {
    const currentFY = financialYearUtils.calculateFinancialYear();
    const [currentStartYear] = currentFY.split("-");
    const baseYear = parseInt(currentStartYear);

    const options = [];

    // Start from 1990 up to next FY
    for (let year = 1990; year <= baseYear + 1; year++) {
      const fy = `${year}-${String(year + 1).slice(-2)}`;
      options.push({
        value: fy,
        label: `FY ${fy}`,
        description: financialYearUtils.getFinancialYearDescription(fy),
      });
    }

    return options.reverse(); // Most recent first
  },

  getExtendedFinancialYearOptions: () => {
    const currentFY = financialYearUtils.calculateFinancialYear();
    const [currentStartYear] = currentFY.split("-");
    const baseYear = parseInt(currentStartYear);

    const options = [];

    // Start from 1990, go up to baseYear + 5
    for (let year = 1990; year <= baseYear + 5; year++) {
      const fy = `${year}-${String(year + 1).slice(-2)}`;
      options.push({
        value: fy,
        label: `FY ${fy}`,
        isCurrent: year === baseYear,
        isPast: year < baseYear,
        isFuture: year > baseYear,
      });
    }

    return options.reverse(); // Most recent first
  },

  // Get actual current financial year (not next year)
  getActualCurrentFinancialYear: () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 1-12

    if (month >= 4) {
      return `${year}-${String(year + 1).slice(-2)}`;
    } else {
      return `${year - 1}-${String(year).slice(-2)}`;
    }
  },

  // Get financial year display text
  getFinancialYearDisplay: (fyCode) => {
    if (!fyCode) return "No Financial Year Selected";
    return `Financial Year: ${fyCode}`;
  },
};

// String utilities - UPDATED getInitials for fullName
export const stringUtils = {
  capitalize: (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  getInitials: (fullName) => {
    if (!fullName) return "?";

    const names = fullName
      .trim()
      .split(" ")
      .filter((n) => n.length > 0);

    if (names.length === 0) return "?";
    if (names.length === 1) return names[0].charAt(0).toUpperCase();

    return (
      names[0].charAt(0).toUpperCase() +
      names[names.length - 1].charAt(0).toUpperCase()
    );
  },

  truncate: (str, length = 50) => {
    if (!str) return "";
    if (str.length <= length) return str;
    return str.substring(0, length) + "...";
  },

  slugify: (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  },
};

// Array utilities
export const arrayUtils = {
  sortBy: (array, key, order = "asc") => {
    return [...array].sort((a, b) => {
      let valueA = a[key];
      let valueB = b[key];

      if (key.includes(".")) {
        const keys = key.split(".");
        valueA = keys.reduce((obj, k) => obj?.[k], a);
        valueB = keys.reduce((obj, k) => obj?.[k], b);
      }

      if (typeof valueA === "string") valueA = valueA.toLowerCase();
      if (typeof valueB === "string") valueB = valueB.toLowerCase();

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });
  },

  filterBySearch: (array, searchTerm, searchFields) => {
    if (!searchTerm) return array;

    const term = searchTerm.toLowerCase();
    return array.filter((item) =>
      searchFields.some((field) => {
        let value = item[field];

        if (field.includes(".")) {
          const keys = field.split(".");
          value = keys.reduce((obj, k) => obj?.[k], item);
        }

        return value && value.toString().toLowerCase().includes(term);
      }),
    );
  },

  groupBy: (array, key) => {
    return array.reduce((groups, item) => {
      let value = item[key];

      if (key.includes(".")) {
        const keys = key.split(".");
        value = keys.reduce((obj, k) => obj?.[k], item);
      }

      const group = value || "Unknown";
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    }, {});
  },

  unique: (array, key = null) => {
    if (!key) {
      return [...new Set(array)];
    }

    const seen = new Set();
    return array.filter((item) => {
      let value = item[key];

      if (key.includes(".")) {
        const keys = key.split(".");
        value = keys.reduce((obj, k) => obj?.[k], item);
      }

      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  },
};

// Validation utilities
export const validationUtils = {
  isEmail: (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  },

  isPhone: (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length >= 10;
  },

  isEmpty: (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  },

  isValidLength: (value, min = 0, max = Infinity) => {
    if (!value) return min === 0;
    const length =
      typeof value === "string" ? value.trim().length : value.length;
    return length >= min && length <= max;
  },
};

// Format utilities
export const formatUtils = {
  fileSize: (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  },

  number: (num) => {
    if (typeof num !== "number") return "0";
    return num.toLocaleString();
  },

  currency: (amount, currency = "INR") => {
    if (typeof amount !== "number") return "₹0";

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
    }).format(amount);
  },

  percentage: (value, total) => {
    if (!total || total === 0) return "0%";
    const percentage = (value / total) * 100;
    return `${Math.round(percentage)}%`;
  },
};

// Color utilities
export const colorUtils = {
  getStatusColor: (status) => {
    const colors = {
      pending: "text-yellow-600 bg-yellow-100",
      active: "text-green-600 bg-green-100",
      inactive: "text-gray-600 bg-gray-100",
      suspended: "text-orange-600 bg-orange-100",
    };
    return colors[status] || "text-gray-600 bg-gray-100";
  },

  getRoleColor: (role) => {
    const colors = {
      admin: "text-purple-600 bg-purple-100",
      department: "text-blue-600 bg-blue-100",
    };
    return colors[role] || "text-gray-600 bg-gray-100";
  },

  generateAvatarColor: (name) => {
    if (!name) return "#6B7280";

    const colors = [
      "#3B82F6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#F97316",
      "#06B6D4",
      "#EC4899",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  },
};

// Local storage utilities
export const storageUtils = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn("Error reading from localStorage:", error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn("Error writing to localStorage:", error);
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn("Error removing from localStorage:", error);
    }
  },

  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn("Error clearing localStorage:", error);
    }
  },
};

// URL utilities
export const urlUtils = {
  getQueryParam: (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  },

  setQueryParam: (param, value) => {
    const url = new URL(window.location);
    url.searchParams.set(param, value);
    window.history.pushState({}, "", url);
  },

  removeQueryParam: (param) => {
    const url = new URL(window.location);
    url.searchParams.delete(param);
    window.history.pushState({}, "", url);
  },

  buildUrl: (base, params = {}) => {
    const url = new URL(base);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  },
};

// Debounce utility
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Default export with all utilities
export default {
  dateUtils,
  financialYearUtils,
  stringUtils,
  arrayUtils,
  validationUtils,
  formatUtils,
  colorUtils,
  storageUtils,
  urlUtils,
  debounce,
};